/*
  Video Slider Gallery v2 — section behaviour.

  Two custom elements:
    <video-slider-gallery-v2>       — section orchestration (Embla, observers,
                                       keyboard, matchMedia pause sweep)
    <video-slider-gallery-v2-slide> — per-slide video state machine

  Every listener attaches through an AbortController signal:
    • Slide-level: one controller per state, aborted on state exit.
    • Gallery-level: one controller for the element's lifetime, aborted on
      disconnect. Covers matchMedia, keyboard, nav buttons.
  Embla's own callbacks are cleaned up by embla.destroy().

  Layout: 100% CSS. SPV (desktop and mobile) is emitted by Liquid as a CSS
  variable; flex-basis derives slide width; aspect-ratio derives slide height.
  No JS layout work. No window-resize handler.
*/

(function () {
  'use strict';

  const SECTION_LOAD_ROOT_MARGIN = '200px 0px';
  const SECTION_LEAVE_THRESHOLD = 0.1;
  const DESKTOP_MQ = '(min-width: 750px)';
  const REDUCED_MOTION_MQ = '(prefers-reduced-motion: reduce)';


  const pad = (n) => String(Math.floor(n)).padStart(2, '0');

  function formatShort(seconds) {
    if (!isFinite(seconds)) return '--:--';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return pad(m) + ':' + pad(s);
  }

  function formatLong(seconds) {
    if (!isFinite(seconds)) return '--:--:--';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    return h + ':' + pad(m) + ':' + pad(s);
  }

  /* ======================================================
     Slide — owns its own state machine.
     ====================================================== */

  class VideoSliderGalleryV2Slide extends HTMLElement {
    constructor() {
      super();
      this.state = 'IDLE';
      this._stateController = null;
      this._playPromiseInFlight = null;
      this._autoplayQueued = false;
      this._timerFormat = 'short';
      this._initialised = false;
    }

    connectedCallback() {
      if (this._initialised) return;
      this._initialised = true;

      this._video = this.querySelector('.video-slider-gallery-v2__video');
      this._timer = this.querySelector('.video-slider-gallery-v2__timer');
      this._muteButton = this.querySelector('.video-slider-gallery-v2__mute-button');
      // Resume position across src release. 0 = no resume (fresh or finished).
      this._savedTime = 0;

      // Host: this carousel, or the Gallery section (gallery-bento.js), which
      // provides the same _sessionMuted / _setSessionMuted / _isSlideCurrent.
      this._parent = this.closest('video-slider-gallery-v2, gallery-bento');
      this._stateController = new AbortController();

      // Constructor sets state='IDLE' without going through _transitionTo, so
      // the switch never fires on initial mount. Wire IDLE controls here so
      // neighbour slides get their click/mute bindings from the first frame.
      this._enterIdle();
    }

    disconnectedCallback() {
      this._abortState();
      this._playPromiseInFlight = null;
    }

    /* ---- Public API (called by the gallery or keyboard handler) ---- */

    assignSrc() {
      // Only the active slide ever holds a src — iOS Safari has a single-video
      // resource constraint, so concurrent <video> srcs starve each other and
      // hang play(). Re-runnable on a released PAUSED slide (src dropped by
      // reset); a live src is a no-op so we don't restart a healthy fetch.
      this._video.preload = 'auto';
      if (this.state !== 'IDLE' && this._video.getAttribute('src')) return;
      const src = this.dataset.videoSrc;
      if (!src) return;
      this._video.src = src;
      // Mobile browsers (iOS WebKit, Android Blink) bind the hardware video
      // decoder to an element lazily — setting .src alone fetches metadata but
      // doesn't always allocate a decoder, so play() resolves but no frames
      // render. resetToPausedOrIdle's load() handles every subsequent visit;
      // this call covers the first visit (slide 0 on initial page load).
      this._video.load();
      this._transitionTo('LOADING_METADATA');
    }

    requestAutoplay() {
      if (this.state === 'IDLE' || this.state === 'LOADING_METADATA') {
        this._autoplayQueued = true;
        return;
      }
      if (this.state === 'READY' || this.state === 'PAUSED') {
        this._transitionTo('ATTEMPTING_PLAY');
      }
      // STALLED / ATTEMPTING_PLAY / PLAYING: play is already in flight.
    }

    togglePlayPause() {
      if (this.state === 'PLAYING' || this.state === 'STALLED') {
        this._video.pause();
      } else {
        // READY/PAUSED route to _transitionTo. IDLE/LOADING_METADATA queue
        // autoplay so a tap during the pre-observer window (active slide
        // mounted but src not yet assigned) still plays once ready.
        this._requestUserPlay();
      }
    }

    toggleMute() {
      this._setMuted(!this._video.muted, true);
    }

    syncMuteFromSession() {
      // Section-wide mute: applies in every state so a user unmuting one slide
      // immediately reflects on every other slide's video element and button.
      // Setting video.muted mid-play just toggles audio output — no restart.
      if (!this._parent) return;
      this._setMuted(!!this._parent._sessionMuted, false);
    }

    resetToPausedOrIdle() {
      this._abortState();
      this._playPromiseInFlight = null;
      this._autoplayQueued = false;

      if (this.state === 'PLAYING' || this.state === 'ATTEMPTING_PLAY' || this.state === 'STALLED') {
        this._video.pause();
      }

      // Release the in-flight network fetch so the next active slide isn't
      // starved. Mutating .preload alone is a no-op per HTML spec — only
      // removing src + load() actually aborts the range request. State stays
      // PAUSED so tap-to-snap, timer pill, ever-played latch all survive.
      // assignSrc is gated to re-run on a PAUSED slide with no src.
      if (this._video.getAttribute('src')) {
        // Snapshot playback position so the next assignSrc can resume.
        // Guard NaN (no metadata yet) and end-of-video (start fresh instead
        // of replaying the last second).
        const t = this._video.currentTime;
        const d = this._video.duration;
        if (isFinite(t) && isFinite(d) && d > 0 && t < d - 1) {
          this._savedTime = t;
        } else {
          this._savedTime = 0;
        }
        this._video.removeAttribute('src');
        this._video.load();
        // Clear the ever-played latch — the video element is now empty, so
        // the thumbnail needs to cover it again. (Latch still survives a
        // user-pause mid-playback, because that path doesn't release src.)
        delete this.dataset.everPlayed;
      }

      // IDLE slides (src never assigned) stay IDLE; everything else → PAUSED.
      const target = this.state === 'IDLE' ? 'IDLE' : 'PAUSED';
      this.state = target;
      this.dataset.state = target;

      this._stateController = new AbortController();
      if (target === 'PAUSED') this._enterPaused();
      else if (target === 'IDLE') this._enterIdle();
    }

    /* ---- Transitions ---- */

    _transitionTo(newState) {
      this._abortState();
      this.state = newState;
      this.dataset.state = newState;
      this._stateController = new AbortController();

      switch (newState) {
        case 'IDLE': this._enterIdle(); break;
        case 'LOADING_METADATA': this._enterLoadingMetadata(); break;
        case 'READY': this._enterReady(); break;
        case 'ATTEMPTING_PLAY': this._enterAttemptingPlay(); break;
        case 'PLAYING': this._enterPlaying(); break;
        case 'PAUSED': this._enterPaused(); break;
        case 'STALLED': this._enterStalled(); break;
      }
    }

    _abortState() {
      if (this._stateController) {
        this._stateController.abort();
        this._stateController = null;
      }
    }

    /* ---- State entry handlers ---- */

    _enterLoadingMetadata() {
      const sig = this._stateController.signal;

      // Metadata arriving early just gives us the duration for timer formatting.
      // It does NOT mean the video can play smoothly — kicking off autoplay here
      // causes stuttering on slow connections. Wait for canplaythrough below.
      this._video.addEventListener('loadedmetadata', () => {
        this._onMetadataLoaded();
      }, { signal: sig, once: true });

      // canplaythrough = browser estimates it can play to the end without
      // stalling at the current bandwidth. Requires preload="auto" on the
      // <video> element so the browser keeps buffering past metadata.
      this._video.addEventListener('canplaythrough', () => {
        this._transitionTo('READY');
      }, { signal: sig, once: true });
    }

    _enterReady() {
      this._renderTimer();
      this._bindPausedControls();

      // If this slide is already the currently-selected one when it reaches
      // READY, sync its mute button so a user who unmuted a prior slide sees
      // the correct icon immediately.
      if (this._parent && typeof this._parent._isSlideCurrent === 'function' && this._parent._isSlideCurrent(this)) {
        this.syncMuteFromSession();
      }

      if (this._autoplayQueued) {
        this._autoplayQueued = false;
        this._transitionTo('ATTEMPTING_PLAY');
      }
    }

    _enterAttemptingPlay() {
      const sig = this._stateController.signal;

      // Section-wide mute: every play (autoplay or user-initiated) honours
      // the gallery's session preference. Default session is muted, so first
      // autoplay is silent. After the user unmutes, every subsequent play
      // (including auto-advance to the next slide) keeps sound on. If the
      // browser blocks unmuted autoplay, the .catch below falls back to PAUSED.
      const shouldMute = this._parent?._sessionMuted === true;
      this._setMuted(!!shouldMute, false);

      // Play-in-flight guard — don't start another play() while one is
      // resolving. Race listeners still attach because the in-flight play
      // will fire `playing`/`timeupdate` on the element and resolve the race.
      if (!this._playPromiseInFlight) {
        const promise = this._video.play();
        this._playPromiseInFlight = promise;

        if (promise && typeof promise.then === 'function') {
          promise.then(() => {
            if (this._playPromiseInFlight === promise) this._playPromiseInFlight = null;
          }).catch(() => {
            const stillApplicable = this.state === 'ATTEMPTING_PLAY' && this._parent && this._parent._isSlideCurrent(this);
            if (this._playPromiseInFlight === promise) this._playPromiseInFlight = null;
            if (stillApplicable) this._transitionTo('PAUSED');
          });
        } else {
          this._playPromiseInFlight = null;
        }
      }

      // Race of two real signals. First fire wins; abort cancels the other.
      // No timeout fallback: if neither fires, the slide stays in
      // ATTEMPTING_PLAY with the spinner visible — the truthful state — and
      // the user can still navigate via arrows/dots.
      let raceWon = false;
      const win = () => {
        if (raceWon) return;
        raceWon = true;
        this._transitionTo('PLAYING');
      };

      this._video.addEventListener('playing', win, { signal: sig });
      this._video.addEventListener('timeupdate', () => {
        if (this._video.currentTime > 0) win();
      }, { signal: sig });
    }

    _enterPlaying() {
      const sig = this._stateController.signal;

      // Reaching PLAYING means a real signal fired (`playing` or
      // `timeupdate` with currentTime > 0), so the first frame has decoded
      // and it's safe to hide the thumbnail immediately.
      this._setEverPlayed();

      this._video.addEventListener('timeupdate', () => this._renderTimer(), { signal: sig });

      this._video.addEventListener('pause', () => {
        if (this.state === 'PLAYING') this._transitionTo('PAUSED');
      }, { signal: sig });

      this._video.addEventListener('waiting', () => {
        if (this.state === 'PLAYING') this._transitionTo('STALLED');
      }, { signal: sig });

      // Natural end clears resume so the next view starts fresh, not at
      // duration-1 (which the resetToPausedOrIdle guard would block anyway,
      // but this is explicit and survives any future guard changes).
      this._video.addEventListener('ended', () => {
        this._savedTime = 0;
      }, { signal: sig });

      // Tap anywhere on the slide to pause. Mute/play buttons are CSS-hidden
      // during PLAYING but the selector guards in case anything lands on them.
      this.addEventListener('click', (e) => {
        if (e.target.closest('.video-slider-gallery-v2__mute-button, .video-slider-gallery-v2__play-button')) return;
        this._video.pause();
      }, { signal: sig });
    }

    _enterIdle() {
      // Neighbour slides never get a src (iOS single-decoder constraint), so
      // they stay IDLE forever. Bind the same click/mute controls a PAUSED
      // slide gets so users can tap play to snap+autoplay, or tap mute to
      // flip the session. Session-mute sync keeps the icon honest.
      this._bindPausedControls();
      this.syncMuteFromSession();
    }

    _enterPaused() {
      const sig = this._stateController.signal;
      this._renderTimer();
      this._bindPausedControls();

      // OS / lock-screen can resume the element without our command (sleep/wake,
      // control centre, audio focus). Mirror that back into state so the UI
      // doesn't lie. Matches the symmetric `pause` listener in _enterPlaying.
      this._video.addEventListener('playing', () => {
        if (this.state === 'PAUSED') this._transitionTo('PLAYING');
      }, { signal: sig });
    }

    _enterStalled() {
      const sig = this._stateController.signal;
      this._video.addEventListener('playing', () => {
        if (this.state === 'STALLED') this._transitionTo('PLAYING');
      }, { signal: sig, once: true });
      this._video.addEventListener('pause', () => {
        if (this.state === 'STALLED') this._transitionTo('PAUSED');
      }, { signal: sig });
    }

    _bindPausedControls() {
      const sig = this._stateController.signal;
      this.addEventListener('click', (e) => {
        if (e.target.closest('.video-slider-gallery-v2__mute-button')) {
          this.toggleMute();
          return;
        }
        // Non-active slide (e.g. visible neighbour on tablet/loop-wrap) →
        // snap to centre and autoplay. Queueing autoplay BEFORE scrollTo
        // ensures it plays regardless of the section's autoplay setting:
        // _onSelect's autoplay branch is gated on data-autoplay, but a
        // queued _autoplayQueued flag is consumed by _enterReady either way.
        if (this._parent && !this._parent._isSlideCurrent(this) && this._parent._embla) {
          const idx = this._parent._embla.slideNodes().indexOf(this);
          if (idx !== -1) {
            this.requestAutoplay();
            this._parent._embla.scrollTo(idx);
          }
          return;
        }
        // Play button or any empty slide area both request play.
        this._requestUserPlay();
      }, { signal: sig });
    }

    _requestUserPlay() {
      if (this.state === 'READY' || this.state === 'PAUSED') {
        this._transitionTo('ATTEMPTING_PLAY');
        return;
      }
      // Active slide tapped before the IntersectionObserver fired assignSrc
      // (narrow window on initial page load). Kick off the load now and
      // queue autoplay so _enterReady consumes it. LOADING_METADATA is
      // included for the equally narrow race where observer fired first
      // but canplaythrough hasn't arrived yet.
      if (this.state === 'IDLE') {
        this._autoplayQueued = true;
        this.assignSrc();
      } else if (this.state === 'LOADING_METADATA') {
        this._autoplayQueued = true;
      }
    }

    /* ---- Helpers ---- */

    _onMetadataLoaded() {
      const duration = this._video.duration;
      // Strict guard: data-duration-known gates the timer pill's visibility
      // during LOADING_METADATA. Only set when duration is a real, finite,
      // positive number — never on NaN, Infinity, or 0. This is what keeps
      // the loading-state timer from ever showing "--:--".
      if (!isFinite(duration) || duration <= 0) return;
      // Timer format is locked once per slide so a short video never shows 0:00:42.
      this._timerFormat = duration >= 3600 ? 'long' : 'short';
      // Stash so the timer pill survives a src release (when this slide stops
      // being active, video.duration becomes NaN — but we still want to show
      // its duration to tablet shoppers choosing what to watch next).
      this._cachedDuration = duration;
      this.dataset.durationKnown = 'true';
      // Resume from saved position with a 1s back-buffer for context.
      // Seeking at loadedmetadata (not canplaythrough) lets the browser
      // buffer FROM the seek point — avoids a double buffer cycle.
      if (this._savedTime > 0) {
        this._video.currentTime = Math.max(0, this._savedTime - 1);
        this._savedTime = 0;
      }
      this._renderTimer();
    }

    _renderTimer() {
      if (!this._timer) return;
      const format = this._timerFormat === 'long' ? formatLong : formatShort;
      const liveDuration = this._video.duration;
      // After src release, video.duration is NaN — fall back to the cached
      // value so the pill keeps showing on inactive visible neighbours.
      const duration = isFinite(liveDuration) ? liveDuration : this._cachedDuration;
      if (!isFinite(duration)) {
        this._timer.textContent = '00:00 / --:--';
        return;
      }
      this._timer.textContent = format(this._video.currentTime) + ' / ' + format(duration);
    }

    _setMuted(muted, syncSession) {
      this._video.muted = muted;
      if (this._muteButton) {
        // aria-pressed reflects whether the unmute action has been pressed.
        // muted=true → unmute action not pressed → aria-pressed="false".
        this._muteButton.setAttribute('aria-pressed', muted ? 'false' : 'true');
        this._muteButton.setAttribute('aria-label', muted ? 'Unmute' : 'Mute');
      }
      if (syncSession && this._parent && typeof this._parent._setSessionMuted === 'function') {
        // Hands the session update to the gallery, which broadcasts to all
        // sibling slides so their video + button stay in lockstep.
        this._parent._setSessionMuted(muted);
      }
    }

    _setEverPlayed() {
      // One-way latch — CSS hides the thumbnail permanently from here.
      this.dataset.everPlayed = 'true';
    }
  }

  customElements.define('video-slider-gallery-v2-slide', VideoSliderGalleryV2Slide);

  /* ======================================================
     Gallery — owns Embla, observers, matchMedia, keyboard,
     dot window.
     ====================================================== */

  class VideoSliderGalleryV2 extends HTMLElement {
    connectedCallback() {
      if (this._initialised) return;
      this._initialised = true;
      try {
        this._init();
      } catch (err) {
        this.dataset.emblaReady = 'failed';
        console.warn('video-slider-gallery-v2 init failed:', err);
      }
    }

    disconnectedCallback() {
      this._teardown();
    }

    _init() {
      // Embla is inlined in the section's <script> tag and runs before this JS,
      // so window.EmblaCarousel is synchronously available here — no await.
      this._emblaNode = this.querySelector('.video-slider-gallery-v2__embla');
      if (!this._emblaNode) return;

      this._announceEl = this.querySelector('.video-slider-gallery-v2__announce');
      this._dotsEl = this.querySelector('.video-slider-gallery-v2__dots');
      this._prevBtn = this.querySelector('.video-slider-gallery-v2__nav--prev');
      this._nextBtn = this.querySelector('.video-slider-gallery-v2__nav--next');

      this._slideCount = Number(this.dataset.slideCount) || 0;

      // Loop gate — the only place data-loop is consulted. Embla's loop wraps
      // believably with at least 3 slides; below that, force off.
      this._loopEnabled = this.dataset.loop === 'true' && this._slideCount >= 3;

      // Per-section mute session. Defaults to muted; flips when the user
      // unmutes. Survives slide changes; does not survive section reload.
      this._sessionMuted = true;
      this._previousSelectedIndex = null;
      this._hasBeenInViewport = false;
      this._resumeSlide = null;
      // Closed until Observer A reports the section near the viewport. While
      // closed, _onSelect must not assign srcs or queue autoplay — Embla's
      // 'init' event fires _onSelect at page load, and for a below-the-fold
      // section that would download slide 0's full video (~2 MB) during the
      // initial render burst. Observer A opens the gate and performs the
      // first assign/autoplay itself.
      this._srcGateOpen = false;
      this._reducedMotion = window.matchMedia(REDUCED_MOTION_MQ).matches;

      this._galleryAbortController = new AbortController();

      this._embla = window.EmblaCarousel(this._emblaNode, {
        align: 'center',
        loop: this._loopEnabled,
        slidesToScroll: 1,
        containScroll: 'trimSnaps',
        dragFree: false,
        // Embla's default is 25 (snappy); 38 gives a softer glide on arrows, swipes and dots.
        duration: this._reducedMotion ? 0 : 38,
        watchDrag: true,
        watchResize: true,
        watchSlides: true
      });

      this._boundOnSelect = () => this._onSelect();
      this._embla.on('select', this._boundOnSelect);
      this._embla.on('init', this._boundOnSelect);

      this._setupNavButtons();
      this._setupSectionObservers();
      this._setupMatchMediaListener();
      this._setupKeyboard();

      // Single source of truth: CSS reads this attribute for all loading-state
      // visuals (skeleton pulse, loading text, arrow/dot visibility).
      this.dataset.emblaReady = 'true';
    }

    _teardown() {
      // Order matters: destroy Embla before aborting the controller so any
      // Embla callbacks that fire during destroy don't reference dead refs.
      if (this._embla) { this._embla.destroy(); this._embla = null; }

      if (this._srcObserver) { this._srcObserver.disconnect(); this._srcObserver = null; }
      if (this._leaveObserver) { this._leaveObserver.disconnect(); this._leaveObserver = null; }

      // Single abort cancels matchMedia, keyboard, nav-button clicks.
      if (this._galleryAbortController) {
        this._galleryAbortController.abort();
        this._galleryAbortController = null;
      }
    }

    /* ---- Embla select routing + dots + nav + announce ---- */

    _onSelect() {
      if (!this._embla) return;

      const nodes = this._embla.slideNodes();
      const incomingIndex = this._embla.selectedScrollSnap();

      // Reset the outgoing slide — pauses any in-flight playback.
      if (this._previousSelectedIndex !== null && this._previousSelectedIndex !== incomingIndex) {
        const outgoing = nodes[this._previousSelectedIndex];
        if (outgoing && typeof outgoing.resetToPausedOrIdle === 'function') {
          outgoing.resetToPausedOrIdle();
        }
      }

      // Only the active slide is assigned a src. iOS Safari can only buffer/
      // decode one <video> at a time — concurrent srcs hang play() silently.
      const incoming = nodes[incomingIndex];

      // Sync mute button on the incoming slide so the icon matches the session
      // before the user taps play.
      if (incoming && typeof incoming.syncMuteFromSession === 'function') {
        incoming.syncMuteFromSession();
      }

      // Src + autoplay only once the section has neared the viewport (gate
      // opened by Observer A). Real swipes/nav can only happen with the
      // section visible, so by then the gate is always open; the only calls
      // this skips are Embla's 'init' at page load. Autoplay (muted) routes
      // through _enterAttemptingPlay which forces mute regardless, so session
      // sync + autoplay ends with the correct state.
      if (this._srcGateOpen) {
        if (incoming && typeof incoming.assignSrc === 'function') {
          incoming.assignSrc();
        }
        if (this.dataset.autoplay === 'true') {
          if (incoming && typeof incoming.requestAutoplay === 'function') {
            incoming.requestAutoplay();
          }
        }
      }

      if (this._announceEl && this._slideCount > 0) {
        this._announceEl.textContent = 'Slide ' + (incomingIndex + 1) + ' of ' + this._slideCount;
      }

      this._renderDots(incomingIndex);
      this._updateNavButtons();

      this._previousSelectedIndex = incomingIndex;
    }

    _updateNavButtons() {
      if (!this._embla) return;
      if (this._prevBtn) this._prevBtn.disabled = !this._embla.canScrollPrev();
      if (this._nextBtn) this._nextBtn.disabled = !this._embla.canScrollNext();
    }

    _isSlideCurrent(slide) {
      if (!this._embla) return false;
      const nodes = this._embla.slideNodes();
      return nodes[this._embla.selectedScrollSnap()] === slide;
    }

    _setSessionMuted(muted) {
      // Single source of truth for runtime mute-session changes. Updates the
      // session field, then broadcasts so every sibling slide's video element
      // and mute button stay in lockstep with the user's intent.
      if (this._sessionMuted === muted) return;
      this._sessionMuted = muted;
      if (!this._embla) return;
      this._embla.slideNodes().forEach((s) => {
        if (typeof s.syncMuteFromSession === 'function') s.syncMuteFromSession();
      });
    }

    /* ---- Gallery-level listeners (all use galleryAbortController) ---- */

    _setupNavButtons() {
      const sig = this._galleryAbortController.signal;
      if (this._prevBtn) {
        this._prevBtn.addEventListener('click', () => {
          if (this._embla) this._embla.scrollPrev();
        }, { signal: sig });
      }
      if (this._nextBtn) {
        this._nextBtn.addEventListener('click', () => {
          if (this._embla) this._embla.scrollNext();
        }, { signal: sig });
      }
    }

    _setupMatchMediaListener() {
      // Breakpoint crossings only: pause any non-IDLE slide through its
      // own reset routine. Embla's reflow can land on a different snap index
      // without firing `select` for the outgoing slide — this sweep prevents
      // the previously-playing video from keeping on silently.
      const mq = window.matchMedia(DESKTOP_MQ);
      const handler = () => {
        if (!this._embla) return;
        const nodes = this._embla.slideNodes();
        nodes.forEach((s) => {
          if (typeof s.resetToPausedOrIdle === 'function') s.resetToPausedOrIdle();
        });
      };
      mq.addEventListener('change', handler, { signal: this._galleryAbortController.signal });
    }

    _setupSectionObservers() {
      // Observer A — the single entry point for the first src assignment:
      // when the section nears the viewport (200px rootMargin), open the src
      // gate, assign the current slide's src and start autoplay. One-shot:
      // disconnects on first fire. Subsequent slides get their src assigned
      // lazily on Embla `select` (see _onSelect), so on slow connections each
      // video downloads with the full pipe to itself instead of competing
      // with siblings in parallel.
      this._srcObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          this._srcGateOpen = true;
          if (this._embla) {
            const nodes = this._embla.slideNodes();
            const current = nodes[this._embla.selectedScrollSnap()];
            if (current && typeof current.assignSrc === 'function') current.assignSrc();
            if (this.dataset.autoplay === 'true' && current && typeof current.requestAutoplay === 'function') {
              current.requestAutoplay();
            }
          }
          if (this._srcObserver) { this._srcObserver.disconnect(); this._srcObserver = null; }
        });
      }, { rootMargin: SECTION_LOAD_ROOT_MARGIN, threshold: 0 });
      this._srcObserver.observe(this);

      // Observer B — pause all playback when the section leaves the viewport.
      // The _hasBeenInViewport gate handles an IntersectionObserver quirk: the
      // first callback after observe() always fires with the element's current
      // state. For a below-the-fold section that means an isIntersecting=false
      // callback BEFORE the section ever appears. Without the gate, that initial
      // fire runs resetToPausedOrIdle on every slide and would wipe the
      // _autoplayQueued flag the moment Observer A (200px rootMargin, so it can
      // fire ahead of B's threshold) has queued autoplay — so autoplay would
      // never fire when the section scrolls into view.
      this._leaveObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (this._hasBeenInViewport && this._embla) {
              // Section returned to view after a prior leave: src was released
              // and Embla 'select' doesn't re-fire on re-entry (slide hasn't
              // moved relative to the embla viewport). Re-assign the active
              // slide so the slider works without requiring a swipe.
              // Resume playback only if this same slide was being watched when
              // the section left (_resumeSlide, set on leave below); a manual
              // pause leaves it paused.
              const nodes = this._embla.slideNodes();
              const active = nodes[this._embla.selectedScrollSnap()];
              if (active) {
                if (typeof active.assignSrc === 'function') active.assignSrc();
                if (this._resumeSlide === active && typeof active.requestAutoplay === 'function') active.requestAutoplay();
                if (typeof active.syncMuteFromSession === 'function') active.syncMuteFromSession();
              }
              this._resumeSlide = null;
            }
            this._hasBeenInViewport = true;
            return;
          }
          if (!this._hasBeenInViewport) return;
          const nodes = this._embla ? this._embla.slideNodes() : [];
          // Record BEFORE the reset wipes state: was the shopper watching? A
          // queued autoplay (slow load) counts. Only ever set here, never
          // cleared, so a quick out-in-out flick can't lose the intent.
          const current = this._embla ? nodes[this._embla.selectedScrollSnap()] : null;
          if (current && (['PLAYING', 'ATTEMPTING_PLAY', 'STALLED'].includes(current.state) || current._autoplayQueued)) {
            this._resumeSlide = current;
          }
          nodes.forEach((s) => {
            if (typeof s.resetToPausedOrIdle === 'function') s.resetToPausedOrIdle();
          });
        });
      }, { threshold: SECTION_LEAVE_THRESHOLD });
      this._leaveObserver.observe(this);
    }

    _setupKeyboard() {
      // Shortcuts are scoped to this section instance — only fire when focus
      // is inside this element (or on it).
      const handler = (e) => {
        const active = document.activeElement;
        if (!active) return;
        if (!this.contains(active) && active !== this) return;

        const tag = active.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || active.isContentEditable) return;

        const nodes = this._embla ? this._embla.slideNodes() : [];
        const current = nodes[this._embla ? this._embla.selectedScrollSnap() : -1];

        switch (e.key) {
          case ' ':
          case 'Spacebar':
            // If a button has focus, let Space trigger its native click.
            if (tag === 'BUTTON') return;
            if (!current || typeof current.togglePlayPause !== 'function') return;
            e.preventDefault();
            current.togglePlayPause();
            break;
          case 'ArrowLeft':
            e.preventDefault();
            if (this._embla) this._embla.scrollPrev();
            break;
          case 'ArrowRight':
            e.preventDefault();
            if (this._embla) this._embla.scrollNext();
            break;
          case 'm':
          case 'M':
            if (!current || typeof current.toggleMute !== 'function') return;
            e.preventDefault();
            current.toggleMute();
            break;
        }
      };
      document.addEventListener('keydown', handler, { signal: this._galleryAbortController.signal });
    }

    /* ---- Dots sliding-window ---- */

    _renderDots(activeIndex) {
      if (!this._dotsEl) return;
      const total = this._slideCount;
      const frag = document.createDocumentFragment();

      if (total <= 5) {
        for (let i = 0; i < total; i++) {
          frag.appendChild(this._buildDot(i === activeIndex));
        }
      } else {
        // 5-dot window, centred on active, clamped inside [0, total-5].
        const winStart = Math.max(0, Math.min(activeIndex - 2, total - 5));
        const hasLeftMore = winStart > 0;
        const hasRightMore = winStart + 4 < total - 1;

        for (let i = 0; i < 5; i++) {
          const dot = this._buildDot(winStart + i === activeIndex);
          if (i === 0 && hasLeftMore) dot.classList.add('video-slider-gallery-v2__dot--edge-2');
          else if (i === 1 && hasLeftMore) dot.classList.add('video-slider-gallery-v2__dot--edge-1');
          else if (i === 4 && hasRightMore) dot.classList.add('video-slider-gallery-v2__dot--edge-2');
          else if (i === 3 && hasRightMore) dot.classList.add('video-slider-gallery-v2__dot--edge-1');
          frag.appendChild(dot);
        }
      }

      this._dotsEl.innerHTML = '';
      this._dotsEl.appendChild(frag);
    }

    _buildDot(isActive) {
      const dot = document.createElement('span');
      dot.className = 'video-slider-gallery-v2__dot';
      if (isActive) dot.classList.add('video-slider-gallery-v2__dot--active');
      return dot;
    }
  }

  customElements.define('video-slider-gallery-v2', VideoSliderGalleryV2);
})();
